const DEVICE_ID_KEY = 'nativePush.deviceId';
const DEVICE_SECRET_KEY = 'nativePush.deviceSecret';
const BINDING_GENERATION_KEY = 'nativePush.bindingGeneration';
let listenersBound = false;
let boundPlugin = null;
let activeBinding = null;
let registrationQueue = Promise.resolve();

function isNativeAndroid() {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true && window.Capacitor?.getPlatform?.() === 'android' && !!window.Capacitor?.Plugins?.PushNotifications;
}

function plugin() { return isNativeAndroid() ? window.Capacitor.Plugins.PushNotifications : null; }

function secureInstallationValue(key) {
  let value = localStorage.getItem(key);
  if (value) return value;
  if (window.crypto?.randomUUID) {
    value = `${window.crypto.randomUUID()}-${window.crypto.randomUUID()}`;
  } else if (window.crypto?.getRandomValues) {
    const bytes = window.crypto.getRandomValues(new Uint8Array(32));
    value = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  } else {
    throw new Error('Secure random generation is unavailable');
  }
  localStorage.setItem(key, value);
  return value;
}

function deviceId() { return secureInstallationValue(DEVICE_ID_KEY); }
function deviceSecret() { return secureInstallationValue(DEVICE_SECRET_KEY); }

function advanceBindingGeneration() {
  const stored = Number.parseInt(localStorage.getItem(BINDING_GENERATION_KEY) || '0', 10);
  const generation = (Number.isSafeInteger(stored) && stored >= 0 ? stored : 0) + 1;
  localStorage.setItem(BINDING_GENERATION_KEY, String(generation));
  return generation;
}

function activateBinding(backendService, authenticatedIdentity = null) {
  if (
    activeBinding?.backend === backendService &&
    activeBinding?.authenticatedIdentity === authenticatedIdentity
  ) return activeBinding;
  activeBinding = {
    backend: backendService,
    authenticatedIdentity,
    generation: advanceBindingGeneration(),
  };
  return activeBinding;
}

async function getPermission() { return (await plugin()?.checkPermissions())?.receive || 'denied'; }

async function bindListeners() {
  const currentPlugin = plugin();
  if (!currentPlugin) return;
  if (listenersBound && boundPlugin === currentPlugin) return;

  activeBinding = null;

  await currentPlugin.createChannel?.({
    id: 'kata_notifications',
    name: 'Kata.fit notifications',
    description: 'Messages, dojo activity, and Coach feedback',
    importance: 4,
    visibility: 0,
  });
  await currentPlugin.addListener('registration', async registration => {
    const binding = activeBinding;
    if (!registration?.value || !binding) return;
    const task = registrationQueue.then(async () => {
      try {
        await binding.backend.registerPushDevice({
          token: registration.value,
          platform: 'android',
          device_id: deviceId(),
          device_secret: deviceSecret(),
          binding_generation: binding.generation,
        });
      } catch (_) {
        window.dispatchEvent(new CustomEvent('kata:native-push-registration-error'));
      }
    });
    registrationQueue = task.catch(() => {});
    return task;
  });
  await currentPlugin.addListener('registrationError', () => window.dispatchEvent(new CustomEvent('kata:native-push-registration-error')));
  await currentPlugin.addListener('pushNotificationActionPerformed', event => window.dispatchEvent(new CustomEvent('kata:native-push-action', { detail: event?.notification?.data || {} })));
  listenersBound = true;
  boundPlugin = currentPlugin;
}

async function bindAuthenticatedUser(backendService, authenticatedIdentity = null) {
  if (!isNativeAndroid()) return { native: false, permission: 'denied' };
  await bindListeners(backendService);
  activateBinding(backendService, authenticatedIdentity);
  const permission = await getPermission();
  if (permission === 'granted') await plugin().register();
  return { native: true, permission };
}

async function enableFromUserGesture(backendService) {
  await bindListeners(backendService);
  const authenticatedIdentity = activeBinding?.backend === backendService
    ? activeBinding.authenticatedIdentity
    : null;
  activateBinding(backendService, authenticatedIdentity);
  let permission = await getPermission();
  if (permission !== 'granted') permission = (await plugin().requestPermissions()).receive;
  if (permission === 'granted') await plugin().register();
  return permission;
}

async function openSettings() { return window.Capacitor?.Plugins?.AndroidNotificationSettings?.open?.(); }

async function unregister(backendService) {
  const native = plugin();
  activeBinding = null;
  const bindingGeneration = advanceBindingGeneration();
  await registrationQueue;
  const [backendResult, nativeResult, clearResult] = await Promise.allSettled([
    backendService.unregisterPushDevice({
      device_id: deviceId(),
      device_secret: deviceSecret(),
      binding_generation: bindingGeneration,
    }),
    native?.unregister?.(),
    native?.removeAllDeliveredNotifications?.(),
  ]);
  if (clearResult.status === 'rejected') throw new Error('Could not securely clear delivered notifications');
  if (backendResult.status === 'rejected' && nativeResult.status === 'rejected') {
    throw new Error('Could not securely revoke push notifications');
  }
  return { revoked: true };
}

const NativePushNotificationService = {
  isNativeAndroid,
  getPermission,
  bindAuthenticatedUser,
  enableFromUserGesture,
  openSettings,
  unregister,
};

export default NativePushNotificationService;
